import { isObject, isNumber, isTruthy, isString } from '../guards/index.js';
import he from '../../node_modules/he/he.js';

const isInputElement = (element, always = false) => {
  return element instanceof HTMLInputElement && (always || element.type !== "hidden" && element.value !== "") || element instanceof HTMLTextAreaElement && (always || element.value !== "") || element instanceof HTMLSelectElement;
};
const isInteractiveRole = (element) => {
  return element instanceof HTMLElement && (element.getAttribute("role") === "button" || element.getAttribute("role") === "link" || element.getAttribute("role") === "menuitem");
};
const isInteractiveElement = (element, always = false) => {
  return element instanceof HTMLAnchorElement || element instanceof HTMLButtonElement || isInteractiveRole(element) || isInputElement(element, always);
};
const getClosestInteractiveElement = (element, always = false) => {
  if (!element) return null;
  if (isInteractiveElement(element, always)) return element;
  return getClosestInteractiveElement(element.parentElement, always);
};
const isEditableElement = (element, always = false) => {
  return isInputElement(element, always) || element instanceof HTMLElement && element.isContentEditable;
};
const isInteractiveElementOrHasInteractiveParent = (element, always = false) => {
  return isInteractiveElement(element, always) || element instanceof HTMLElement && isInteractiveElement(element.closest("a, button, input"), always);
};
const generateSelectorPath = (rootElement, childElement, options) => {
  const {
    includeRootElement = false,
    allowedAttributes = []
  } = options || {};
  if (!rootElement.contains(childElement)) {
    return "";
  }
  if (rootElement === childElement && includeRootElement) {
    return getElementSelector(rootElement, allowedAttributes);
  }
  const path = [];
  let currentElement = childElement;
  while (currentElement) {
    if (currentElement === rootElement) {
      if (includeRootElement) {
        path.push(getElementSelector(currentElement, allowedAttributes));
      }
      break;
    }
    if (currentElement.parentElement) {
      const siblings = Array.from(currentElement.parentElement.children);
      const index = siblings.indexOf(currentElement) + 1;
      let selector = `${currentElement.tagName.toLowerCase()}:nth-child(${index})`;
      if (allowedAttributes.length > 0) {
        selector = addAttributesToSelector(currentElement, selector, allowedAttributes);
      }
      path.push(selector);
    }
    currentElement = currentElement.parentElement;
  }
  path.reverse();
  return path.join(" > ");
};
const getElementSelector = (element, allowedAttributes) => {
  let selector = element.tagName.toLowerCase();
  if (allowedAttributes.length > 0) {
    selector = addAttributesToSelector(element, selector, allowedAttributes);
  }
  return selector;
};
const addAttributesToSelector = (element, selector, allowedAttributes) => {
  let result = selector;
  for (const attr of allowedAttributes) {
    const value = element.getAttribute(attr);
    if (value !== null) {
      result += `[${attr}="${value}"]`;
    }
  }
  return result;
};
const getNearestScrollableParent = (element) => {
  if (!element) return null;
  const effectiveHeight = element instanceof HTMLElement ? Math.max(element.offsetHeight, element.clientHeight) : element.clientHeight;
  const tolerance = 2;
  if (element.scrollHeight > effectiveHeight + tolerance) return element;
  if (element.parentElement) {
    return getNearestScrollableParent(element.parentElement);
  }
  return null;
};
const getCombinedRect = (elements) => {
  if (elements.length === 0) return null;
  const rects = elements.map((element) => {
    if (element instanceof Element) {
      return element.getBoundingClientRect();
    }
    if (isObject(element) && "left" in element && "top" in element && "right" in element && "bottom" in element && isNumber(element.left) && isNumber(element.top) && isNumber(element.right) && isNumber(element.bottom)) {
      return {
        left: element.left,
        top: element.top,
        right: element.right,
        bottom: element.bottom,
        width: Math.abs(element.right - element.left),
        height: Math.abs(element.bottom - element.top),
        x: Math.min(element.left, element.right),
        y: Math.min(element.top, element.bottom)
      };
    }
    return null;
  }).filter(isTruthy);
  if (rects.length === 0) return null;
  const combinedRect = rects.reduce((acc, rect) => {
    const left = Math.min(acc.left, rect.left);
    const right = Math.max(acc.right, rect.right);
    const top = Math.min(acc.top, rect.top);
    const bottom = Math.max(acc.bottom, rect.bottom);
    const obj = {
      left,
      top,
      right,
      bottom,
      width: Math.abs(right - left),
      height: Math.abs(bottom - top),
      x: Math.min(left, right),
      y: Math.min(top, bottom)
    };
    return {
      ...obj,
      toJSON: () => obj
    };
  }, rects[0]);
  return combinedRect;
};
const escapeAttribute = (value) => {
  if (!isString(value)) return "";
  return he.encode(value, {
    useNamedReferences: true
  });
};
const attributesToString = (attributes) => {
  return Object.entries(attributes).map(([key, value]) => `${key}="${escapeAttribute(value)}"`).join(" ");
};
const getTextNodesWithinSelection = (selection) => {
  if (selection.rangeCount === 0) return [];
  const range = selection.getRangeAt(0);
  const textNodes = /* @__PURE__ */ new Set();
  const root = range.commonAncestorContainer instanceof Element ? range.commonAncestorContainer : range.commonAncestorContainer.parentElement;
  if (!root) return [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      if (range.intersectsNode(node)) return NodeFilter.FILTER_ACCEPT;
      return NodeFilter.FILTER_REJECT;
    }
  });
  while (walker.nextNode()) {
    textNodes.add(walker.currentNode);
  }
  return Array.from(textNodes);
};
const getRangeForElements = (elements) => {
  if (!elements || elements.length === 0) return null;
  let firstElement = elements[0];
  let lastElement = elements[0];
  for (let i = 1; i < elements.length; i++) {
    const el = elements[i];
    if (firstElement.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING) {
      firstElement = el;
    } else if (lastElement.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING) {
      lastElement = el;
    }
  }
  const range = document.createRange();
  range.setStartBefore(firstElement);
  range.setEndAfter(lastElement);
  return range;
};

export { attributesToString, escapeAttribute, generateSelectorPath, getClosestInteractiveElement, getCombinedRect, getNearestScrollableParent, getRangeForElements, getTextNodesWithinSelection, isEditableElement, isInputElement, isInteractiveElement, isInteractiveElementOrHasInteractiveParent, isInteractiveRole };
//# sourceMappingURL=index.js.map
